<script setup lang="ts">
import Popover from 'primevue/popover'
import { computed, nextTick, ref, useId } from 'vue'

import { can } from '@/app/acl'
import { useI18n } from '@/app/i18n'
import { switchCompany } from '@/app/session'
import { useAuthStore } from '@/app/stores/auth'
import { useCountsStore } from '@/app/stores/counts'
import { errorMessage, toast } from '@/shared/composables/useToast'
import AppAvatar from '@/shared/ui/AppAvatar.vue'
import AppBadge from '@/shared/ui/AppBadge.vue'
import AppIcon from '@/shared/ui/AppIcon.vue'
import { workspaceInitials } from '@/shared/utils/initials'

import AddOrganizationModal from './AddOrganizationModal.vue'
import { foldText } from './navbar-items'

/**
 * The navbar's workspace button (wp/shell §4.16, redesigned 2026-10): the workspace's avatar (logo or initials) and
 * name, opening a list of the user's workspaces — with a filter once there are many — and, under it, "Add new
 * organization" and "Organizations". Choosing a workspace calls `main/switchCompany` and reloads the same URL
 * (OPEN-4 (a)). FX-057 (intended, legacy showed both to every non-Freelancer): "+" for Clients only (the API refuses
 * Staff), the organizations entry only with `company-profile` read (the guard refuses the page otherwise, D-21).
 * Below 992 px the name is hidden and the avatar alone opens the list.
 * For a person on several workspaces, each other workspace shows its unread chat messages (`chat/unreadByWorkspace`,
 * the counts store), and the button a dot when any of them has some: a message sent there is not missed.
 */
defineProps<{ lg: boolean }>()
const { t } = useI18n()
const auth = useAuthStore()
const badges = useCountsStore()
const adding = ref(false)
const popover = ref<InstanceType<typeof Popover> | null>(null)
const open = ref(false)
const filter = ref('')
const filterInput = ref<HTMLInputElement | null>(null)
const listId = `workspaces-${useId()}`

/** Above this many workspaces the list gets a filter field (a Staff member can manage dozens). */
const FILTER_FROM = 8

const workspace = computed(() => auth.currentWorkspace)
const initials = computed(() => workspaceInitials(workspace.value?.Label))
const options = computed(() =>
  (auth.session?.workspaces ?? []).map((entry) => ({ value: entry.Id, text: entry.Label ?? String(entry.Id) })),
)
const filterable = computed(() => options.value.length >= FILTER_FROM)
/** Unread in a workspace other than the current one (the current one has the Messages badge). */
const unreadIn = (id: number) => (id === workspace.value?.Id ? 0 : badges.workspaceBadge(id))
const unreadElsewhere = computed(() => options.value.some((option) => unreadIn(option.value) > 0))
const shown = computed(() => {
  const query = foldText(filter.value)
  return query ? options.value.filter((option) => foldText(option.text).includes(query)) : options.value
})
const canAdd = computed(() => auth.userType === 'Client')
const canOpenOrganizations = computed(() => auth.userType !== 'Freelancer' && can('company-profile'))
/** Nothing to open for a single workspace without the actions: the button is static. */
const switchable = computed(() => options.value.length > 1 || canAdd.value || canOpenOrganizations.value)

function toggle(event: Event): void {
  if (!switchable.value) return
  popover.value?.toggle(event)
}

function onShow(): void {
  open.value = true
  filter.value = ''
  if (filterable.value) void nextTick(() => filterInput.value?.focus())
}

async function choose(id: number | null | undefined): Promise<void> {
  popover.value?.hide()
  if (!id || id === workspace.value?.Id) return
  try {
    await switchCompany(id)
  } catch (error) {
    // Legacy had no catch; the select would show a workspace the server did not switch to.
    toast.danger(errorMessage(error))
  }
}

function add(): void {
  popover.value?.hide()
  adding.value = true
}
</script>

<template>
  <div v-if="workspace" class="workspace-switcher" data-testid="workspace-switcher">
    <button
      type="button"
      class="workspace-switcher__button"
      :class="{ 'is-static': !switchable }"
      :aria-label="lg ? undefined : workspace.Label ?? t('shell.navbar.action.workspaces')"
      :aria-haspopup="switchable ? 'listbox' : undefined"
      :aria-expanded="switchable ? open : undefined"
      :aria-controls="switchable && open ? listId : undefined"
      data-testid="workspace-switcher-open"
      @click="toggle"
    >
      <AppAvatar :src="workspace.LogoUrl ?? null" :text="initials" :size="28" class="workspace-switcher__avatar" />
      <span v-if="lg" class="workspace-switcher__name" data-testid="workspace-switcher-name">{{ workspace.Label }}</span>
      <AppIcon v-if="switchable && lg" name="ChevronsUpDownIcon" :size="16" class="workspace-switcher__chevron" />
      <span v-if="unreadElsewhere" class="workspace-switcher__dot" data-testid="workspace-unread-dot" aria-hidden="true" />
    </button>

    <Popover
      v-if="switchable"
      ref="popover"
      :base-z-index="1100"
      :pt="{ root: { class: 'shell-popover navbar-panel workspace-switcher__panel', 'data-testid': 'workspace-list' } }"
      @show="onShow"
      @hide="open = false"
    >
      <div class="navbar-panel__heading">{{ t('shell.navbar.action.workspaces') }}</div>
      <label v-if="filterable" class="workspace-switcher__filter">
        <AppIcon name="SearchIcon" :size="15" />
        <input
          ref="filterInput"
          v-model="filter"
          type="search"
          :aria-label="t('shell.navbar.action.search')"
          autocomplete="off"
          data-testid="workspace-filter"
        />
      </label>
      <ul
        :id="listId"
        role="listbox"
        :aria-label="t('shell.navbar.action.workspaces')"
        class="navbar-panel__list workspace-switcher__list"
      >
        <li v-for="option in shown" :key="option.value" role="presentation">
          <button
            type="button"
            role="option"
            class="navbar-panel__row"
            :class="{ 'is-current': option.value === workspace.Id }"
            :aria-selected="option.value === workspace.Id"
            :data-workspace="option.value"
            @click="choose(option.value)"
          >
            <AppAvatar :text="workspaceInitials(option.text)" :size="28" class="workspace-switcher__avatar" />
            <span class="navbar-panel__row-text">
              <span class="navbar-panel__row-title">{{ option.text }}</span>
            </span>
            <AppBadge
              v-if="unreadIn(option.value)"
              :value="unreadIn(option.value)"
              variant="danger"
              pill
              data-testid="workspace-unread"
            />
            <AppIcon v-if="option.value === workspace.Id" name="CheckIcon" :size="18" class="navbar-panel__check" />
          </button>
        </li>
      </ul>
      <div v-if="canAdd || canOpenOrganizations" class="navbar-panel__footer">
        <button v-if="canAdd" type="button" class="navbar-panel__row" data-testid="add-organization" @click="add">
          <AppIcon name="PlusIcon" :size="18" class="navbar-panel__row-icon" />
          <span class="navbar-panel__row-text">
            <span class="navbar-panel__row-title">{{ t('shell.modal.addOrganization.title.page') }}</span>
          </span>
        </button>
        <RouterLink
          v-if="canOpenOrganizations"
          to="/organizations"
          class="navbar-panel__row"
          data-testid="organizations"
          @click="popover?.hide()"
        >
          <AppIcon name="SettingsIcon" :size="18" class="navbar-panel__row-icon" />
          <span class="navbar-panel__row-text">
            <span class="navbar-panel__row-title">{{ t('shell.navbar.action.organizations') }}</span>
          </span>
        </RouterLink>
      </div>
    </Popover>
    <AddOrganizationModal v-if="canAdd" v-model:visible="adding" />
  </div>
</template>

<style scoped>
.workspace-switcher {
  min-width: 0;
}
.workspace-switcher__button {
  position: relative;
  display: flex;
  gap: 0.6rem;
  align-items: center;
  max-width: 320px;
  height: 40px;
  padding: 0 0.6rem 0 0.4rem;
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--p-border-radius-lg);
  transition: background 0.2s ease;
}
.workspace-switcher__button:hover:not(.is-static),
.workspace-switcher__button[aria-expanded='true'] {
  background: var(--app-fill);
}
.workspace-switcher__dot {
  position: absolute;
  top: 5px;
  left: 28px;
  width: 9px;
  height: 9px;
  background: var(--p-red-500, #ea5455);
  border: 2px solid var(--p-content-background);
  border-radius: 50%;
}
.workspace-switcher__button.is-static {
  cursor: default;
}
.workspace-switcher__button:focus-visible {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 2px;
}
.workspace-switcher__avatar {
  flex: 0 0 auto;
}
/* The current workspace's avatar gets a 2px ring in the page colour (white, the dark surface in dark mode). */
.workspace-switcher__button > .workspace-switcher__avatar {
  box-shadow: 0 0 0 2px var(--p-content-background);
}
.workspace-switcher__name {
  min-width: 0;
  overflow: hidden;
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-surface-950);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.workspace-switcher__chevron {
  flex: 0 0 auto;
  color: var(--p-surface-500);
}
.workspace-switcher__filter {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin: 0 0.25rem 0.4rem;
  padding: 0 0.6rem;
  color: var(--p-surface-500);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
}
.workspace-switcher__filter input {
  flex: 1 1 auto;
  min-width: 0;
  height: 32px;
  font: inherit;
  font-size: 0.929rem;
  color: var(--p-text-color);
  background: transparent;
  border: 0;
  outline: none;
}
.workspace-switcher__list {
  max-height: 50vh;
  overflow-y: auto;
}
</style>
